import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, ShoppingCart, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SalesOrders() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [newOrder, setNewOrder] = useState({
    customer_id: '',
    order_date: new Date().toISOString().split('T')[0],
    expected_delivery_date: '',
    items: [{ description: '', hsn_code: '998314', quantity: 1, unit_price: 0 }],
    notes: ''
  });

  useEffect(() => { fetchData(); }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ordersRes, custRes] = await Promise.all([
        fetch(`${API}/api/sales/orders`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/api/sales/customers`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      if (ordersRes.ok) setOrders((await ordersRes.json()).orders || []);
      if (custRes.ok) {
        const data = await custRes.json();
        setCustomers([...(data.customers || []), ...(data.sub_customers || []).map(s => ({ ...s, customer_id: s.sub_customer_id }))]);
      }
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const createOrder = async () => {
    if (!newOrder.customer_id || !newOrder.items[0].description) {
      toast.error('Please fill required fields');
      return;
    }
    setProcessing(true);
    try {
      const res = await fetch(`${API}/api/sales/orders`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newOrder)
      });
      if (res.ok) {
        toast.success('Sales order created');
        setShowNew(false);
        setNewOrder({ customer_id: '', order_date: new Date().toISOString().split('T')[0], expected_delivery_date: '', items: [{ description: '', hsn_code: '998314', quantity: 1, unit_price: 0 }], notes: '' });
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create order');
      }
    } catch (error) { toast.error('Failed to create order'); }
    finally { setProcessing(false); }
  };

  const formatCurrency = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(amt || 0);

  if (loading) return <div className="flex justify-center p-8"><RefreshCw className="h-6 w-6 animate-spin" /></div>;

  return (
    <Card className="mt-4">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center gap-2"><ShoppingCart className="h-5 w-5" />Sales Orders</CardTitle>
          <Dialog open={showNew} onOpenChange={setShowNew}>
            <DialogTrigger asChild><Button><Plus className="h-4 w-4 mr-2" />New Order</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Create Sales Order</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Customer *</Label>
                  <Select value={newOrder.customer_id} onValueChange={(v) => setNewOrder({ ...newOrder, customer_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
                    <SelectContent>
                      {customers.map(c => <SelectItem key={c.customer_id} value={c.customer_id}>{c.company_name || c.contact_person}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Order Date</Label><Input type="date" value={newOrder.order_date} onChange={(e) => setNewOrder({ ...newOrder, order_date: e.target.value })} /></div>
                  <div><Label>Expected Delivery</Label><Input type="date" value={newOrder.expected_delivery_date} onChange={(e) => setNewOrder({ ...newOrder, expected_delivery_date: e.target.value })} /></div>
                </div>
                <div className="border rounded p-3 bg-slate-50">
                  <Label className="text-sm font-medium">Line Item</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div><Label className="text-xs">Description *</Label><Input value={newOrder.items[0].description} onChange={(e) => setNewOrder({ ...newOrder, items: [{ ...newOrder.items[0], description: e.target.value }] })} /></div>
                    <div><Label className="text-xs">HSN</Label><Input value={newOrder.items[0].hsn_code} onChange={(e) => setNewOrder({ ...newOrder, items: [{ ...newOrder.items[0], hsn_code: e.target.value }] })} /></div>
                    <div><Label className="text-xs">Qty</Label><Input type="number" min="1" value={newOrder.items[0].quantity} onChange={(e) => setNewOrder({ ...newOrder, items: [{ ...newOrder.items[0], quantity: parseInt(e.target.value) || 1 }] })} /></div>
                    <div><Label className="text-xs">Unit Price</Label><Input type="number" min="0" value={newOrder.items[0].unit_price} onChange={(e) => setNewOrder({ ...newOrder, items: [{ ...newOrder.items[0], unit_price: parseFloat(e.target.value) || 0 }] })} /></div>
                  </div>
                </div>
                <div><Label>Notes</Label><Textarea value={newOrder.notes} onChange={(e) => setNewOrder({ ...newOrder, notes: e.target.value })} rows={2} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
                <Button onClick={createOrder} disabled={processing}>{processing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}Create Order</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order #</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Invoiced</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.map((o) => (
              <TableRow key={o.order_id}>
                <TableCell className="font-medium">{o.order_number}</TableCell>
                <TableCell>{o.order_date}</TableCell>
                <TableCell>{o.customer_name}</TableCell>
                <TableCell className="text-right">{formatCurrency(o.total_amount)}</TableCell>
                <TableCell><Badge variant="outline">{o.status}</Badge></TableCell>
                <TableCell>{o.invoiced ? <Badge className="bg-green-100 text-green-700">Yes</Badge> : <Badge variant="secondary">No</Badge>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {orders.length === 0 && <div className="text-center py-8 text-slate-500">No sales orders found</div>}
      </CardContent>
    </Card>
  );
}
